import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import NavBar from './NavBar'
import TopBar from './TopBar';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import theme from '../theme';
import RegisterView from '../views/auth/Register';
import ConfirmEmail from '../views/auth/ConfirmEmail';
import PolicyPage from '../views/policies/PolicyPage';
import {
    COOKIE_POLICY_PATH,
    LOGIN_PATH,
    PRIVACY_POLICY_PATH,
    PUBLIC_APP_TAB_PATHS,
} from '../components/LegalLinks';
import RequestPassword from '../views/auth/ForgotPasswordRequest';
import ForgotPassword from '../views/auth/ForgotPasswordSubmit';
import { useDispatch, useSelector } from 'react-redux';
import MobileMenu from "./MobileMenu";
import AppAuthenticatedRoutes from '../components/AppAuthenticatedRoutes';
import { useSessionBootstrap } from '../hooks/useAuth';
import { useBotmarkCompanyInfo } from '../hooks/useCompanies';
import TabPanel from '../components/TabPanel';
import { usePermissionStore } from '../stores/permissionStore';

import HomePage from "../views/auth/HomePage";
import LoginPage from "../views/auth/LoginPage";
import HomeTutorial from "../views/auth/HomeTutorial";
import HomeAbout from "../views/auth/HomeAbout";
import HomeContact from "../views/auth/HomeContact";
import "./Layout.css";
import {
    clearAuthSession,
    isStoredSessionExpired,
    resetSessionExpiryGuard,
} from '../mixins/authSession';

/* AppBar is fixed; panel height fills the viewport below the toolbar (56 mobile / 64 desktop). */
const BELOW_APP_BAR_MIN_HEIGHT = {
    xs: 'calc(100vh - 56px)',
    md: 'calc(100vh - 64px)',
};

/*
 * Portal shell: menu and page content size independently. The row is as tall as
 * the taller of the two, so closing the mobile menu does not clip the page, and
 * tall admin forms are not trapped in a viewport-height inner scroller.
 */
const Paper = styled(Box)({
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
})

const Wrapper = styled(Box)({
    display: 'flex',
    flex: 1,
    minWidth: 0,
    overflow: 'visible',
})

const ContentContainer = styled(Box)({
    display: 'flex',
    flex: 1,
    minWidth: 0,
    width: '100%',
    overflow: 'visible',
})

const Content = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    width: '100%',
    overflow: 'visible',
    paddingBottom: 24,
})

const TAB_PANELS = {
    HOME: 0,
    TUTORIAL: 1,
    ABOUT: 2,
    CONTACT: 3,
    APP: 4,
}

const MainLayout = () => {
    const [isMobileNavOpen, setMobileNavOpen] = useState(true);
    const authenticated = useSelector((state) => state.UserReducer.isAuthenticated);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    useSessionBootstrap();
    useBotmarkCompanyInfo();

    const getInitialTab = () => {
        const s = sessionStorage.getItem('lastTab');
        const n = parseInt(s, 10);
        return (s !== null && !isNaN(n) && n >= TAB_PANELS.HOME && n <= TAB_PANELS.APP) ? n : TAB_PANELS.HOME;
    };
    const [value, setValue] = React.useState(getInitialTab);

    React.useEffect(() => {
        if (location.pathname === '/logout') {
            return;
        }

        const auth = sessionStorage.getItem('auth');
        if (auth && isStoredSessionExpired()) {
            clearAuthSession(dispatch);
            navigate(LOGIN_PATH);
        }
    }, [location.pathname, dispatch, navigate]);

    React.useEffect(() => {
        const auth = sessionStorage.getItem('auth');
        if (!auth) {
            return;
        }

        if (isStoredSessionExpired()) {
            clearAuthSession(dispatch);
            return;
        }

        const info = JSON.parse(auth);
        resetSessionExpiryGuard();
        dispatch({ type: 'IS_AUTHENTICATED', payload: info });
    }, [dispatch]);

    React.useEffect(() => {
        if (location.pathname === '/logout') {
            sessionStorage.removeItem('auth');
            dispatch({ type: 'IS_AUTHENTICATED', payload: false });
            dispatch({ type: 'LOGGED_OUT', payload: false });
            usePermissionStore.getState().clearPermissions();
            navigate(LOGIN_PATH);
        }
    }, [location.pathname, dispatch, navigate]);

    React.useEffect(() => {
        if (PUBLIC_APP_TAB_PATHS.includes(location.pathname)) {
            setValue(TAB_PANELS.APP);
        }
    }, [location.pathname]);

    React.useEffect(() => {
        if (!authenticated && value === TAB_PANELS.APP && !PUBLIC_APP_TAB_PATHS.includes(location.pathname)) {
            navigate(LOGIN_PATH);
        }
    }, [authenticated, value, location.pathname, navigate]);

    const handleTabChange = (event, newValue) => {
          setValue(newValue);
          sessionStorage.setItem('lastTab', String(newValue));
          if (newValue === TAB_PANELS.APP) {
            setMobileNavOpen(true)
            if (!authenticated && !PUBLIC_APP_TAB_PATHS.includes(location.pathname)) {
                navigate(LOGIN_PATH);
            }
          } else {
            setMobileNavOpen(false)
            if (newValue === TAB_PANELS.HOME) {
                navigate('/');
            }
          }
      };
      const handleMobleTabChange = (newValue) => {
          setValue(newValue);
          sessionStorage.setItem('lastTab', String(newValue));
          if (newValue === TAB_PANELS.APP) {
            setMobileNavOpen(true)
            if (!authenticated && !PUBLIC_APP_TAB_PATHS.includes(location.pathname)) {
                navigate(LOGIN_PATH);
            }
          } else if (newValue === TAB_PANELS.HOME) {
            navigate('/');
          } else {
            setMobileNavOpen(false)
          }
      };

    const isPortalShell = Boolean(authenticated) && value === TAB_PANELS.APP;

    return (
        <Paper
            sx={
                isPortalShell
                    ? { minHeight: '100vh', overflow: 'visible' }
                    : {
                        height: '100vh',
                        overflow: 'hidden',
                        '& .tabPanel': {
                            height: BELOW_APP_BAR_MIN_HEIGHT,
                            minHeight: 0,
                        },
                    }
            }
        >
            <TopBar value={value} handleTabChange={handleTabChange} handleMobleTabChange={handleMobleTabChange} setMobileNavOpen={setMobileNavOpen} isMobileNavOpen={isMobileNavOpen}/>
            <TabPanel unmountInactive padding={0} className="tabPanel" value={value} index={TAB_PANELS.HOME} style={{width:"100%", backgroundColor:'black', overflowY:'auto', scrollbarWidth:'none'}}>
                <HomePage/>
            </TabPanel>
            <TabPanel unmountInactive padding={0} className="tabPanel" value={value} index={TAB_PANELS.TUTORIAL} style={{width:"100%", overflowY:'auto', scrollbarWidth:'none', backgroundColor:"rgb(33, 33, 33)"}}>
                <HomeTutorial/>
            </TabPanel>
            <TabPanel unmountInactive padding={0} className="tabPanel" value={value} index={TAB_PANELS.ABOUT} style={{width:"100%", overflowY:"auto", scrollbarWidth:"none"}}>
                <HomeAbout/>
            </TabPanel>
            <TabPanel unmountInactive padding={0} className="tabPanel" value={value} index={TAB_PANELS.CONTACT} style={{width:"100%"}}>
                <HomeContact/>
            </TabPanel>
            { !authenticated ?
                <TabPanel unmountInactive padding={0} className="tabPanel" value={value} index={TAB_PANELS.APP} style={{width:"100%", overflowY:'scroll', scrollbarWidth:'none'}}>
                    <Routes>
                        <Route path={LOGIN_PATH} element={<LoginPage/>}/>
                        <Route path='/logout'/>
                        <Route path='/register' element={<RegisterView/>}/>
                        <Route path='/ConfirmEmail' element={<ConfirmEmail/>}/>
                        <Route path='/passwordreset' element={<ForgotPassword/>}/>
                        <Route path='/requestpassword' element={<RequestPassword/>}/>
                        <Route path={PRIVACY_POLICY_PATH} element={<PolicyPage type="privacy"/>}/>
                        <Route path={COOKIE_POLICY_PATH} element={<PolicyPage type="cookies"/>}/>
                    </Routes>
                </TabPanel>
            :
                <TabPanel
                    unmountInactive
                    padding={0}
                    fillHeight={false}
                    className="tabPanel"
                    value={value}
                    index={TAB_PANELS.APP}
                    sx={{
                        width: '100%',
                        overflow: 'visible',
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            width: '100%',
                        }}
                    >
                        <Box sx={{ flexShrink: 0 }}>
                            <MobileMenu className='mobileMenuDiv' setMobileNavOpen={setMobileNavOpen} isMobileNavOpen={isMobileNavOpen}/>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'stretch',
                                flex: 1,
                                width: '100%',
                                minWidth: 0,
                            }}
                        >
                            <NavBar className="sideMenu" setMobileNavOpen={setMobileNavOpen} isMobileNavOpen={!isMobileNavOpen}/>
                            <Wrapper>
                                <ContentContainer>
                                    <Content>
                                        <AppAuthenticatedRoutes />
                                    </Content>
                                </ContentContainer>
                            </Wrapper>
                        </Box>
                    </Box>
                </TabPanel>
}
        </Paper>
  );
};

export default MainLayout;
